/** Pure helpers shared by the submission form. */

/** Shortest slug the broker accepts (`SubmissionMetadata.slug` min_length). */
export const MIN_SLUG_LENGTH = 2

/** Longest slug the broker accepts (`SubmissionMetadata.slug` max_length). */
export const MAX_SLUG_LENGTH = 40

/** Most tags the broker accepts on one submission. */
export const MAX_TAGS = 8

/** Mirrors the broker's slug and tag validators. */
export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/

/** Longest tag the broker accepts (`_validate_tags`). */
export const MAX_TAG_LENGTH = 30

/**
 * Mirrors the broker's `SAFE_FILENAME_PATTERN` (`api/v1/common/submission.py`).
 *
 * The filename becomes a segment of the GCS object key, so the broker allows only
 * a leading alphanumeric followed by alphanumerics, `.`, `_` and `-`. Routine
 * download names — `My App.html`, `dashboard (1).html`, `café.html` — violate it.
 */
export const SAFE_FILENAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/

/**
 * Rewrite a filename into one the broker will accept.
 *
 * Offered as a suggestion in the error message rather than applied silently: the
 * name ends up in the object key, so quietly changing it would make the stored
 * object disagree with the file the submitter chose.
 */
export function suggestSafeFilename(name: string): string {
  return (
    name
      .replace(/[^A-Za-z0-9._-]+/g, '-')
      .replace(/^[^A-Za-z0-9]+/, '')
      .replace(/-+/g, '-') || 'app.html'
  )
}

/**
 * Derive a URL slug from an app name.
 *
 * Trailing hyphens are stripped after truncation as well as before it, so a
 * name whose 40-character cut lands on a separator still yields a valid slug.
 */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/g, '')
}

/** Format a byte count for display in a size limit or error message. */
export function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  return mb >= 1
    ? `${Math.round(mb * 10) / 10} MB`
    : `${Math.round(bytes / 1024)} KB`
}

/**
 * Validate the chosen app file, returning a message or null when it is usable.
 *
 * Lives here rather than inside the form so it is unit-testable without
 * rendering: a submission is one self-contained .html or .md file, and each
 * rule below mirrors a bound the broker enforces server-side.
 */
export function validateAppFile(file: File | null, maxBytes: number): string | null {
  if (!file) return 'Choose the app file to submit.'
  if (!/\.(html|md)$/i.test(file.name)) {
    return 'The app must be a single .html or .md file.'
  }
  // The filename is the one submission value the form cannot edit, and it is sent
  // verbatim as the object-key segment — so mirror the broker's rule here rather
  // than let a space or an accent come back as a 422 after the upload starts.
  if (!SAFE_FILENAME_PATTERN.test(file.name)) {
    return (
      `Rename the file to "${suggestSafeFilename(file.name)}" — the name may only ` +
      "contain letters, numbers, '.', '_' and '-', and must start with a letter or number."
    )
  }
  if (file.size === 0) return 'That file is empty.'
  if (file.size > maxBytes) {
    return `That file is ${formatBytes(file.size)}; the limit is ${formatBytes(maxBytes)}.`
  }
  return null
}

/**
 * MIME type to declare for the upload, derived from the file's extension.
 *
 * Mirrors the broker's `ALLOWED_UPLOAD_CONTENT_TYPES` — a `.md` file must
 * declare `text/markdown` or the broker rejects it before the upload starts.
 * Allowlisted rather than defaulted, so widening `validateAppFile` alone
 * surfaces here instead of silently declaring the new shape as HTML.
 */
export function contentTypeForFilename(name: string): string {
  if (/\.md$/i.test(name)) return 'text/markdown'
  if (/\.html$/i.test(name)) return 'text/html'
  throw new Error(`Unsupported file extension: ${name}`)
}

/** Split a comma-separated tag input into normalized tags. */
export function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map((tag) => tag.trim().toLowerCase())
    .filter((tag) => tag.length > 0)
}

/** Present an enum wire value as a readable label without diverging from it. */
export function humanizeOption(value: string): string {
  const spaced = value.replace(/-/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
